"use client";
import { motion } from "framer-motion";
import { Car, Plane, Hotel, MapPin, Headphones, ShieldCheck } from "lucide-react";
import about from "../accests/common/ab.jpeg";
import abb from "../accests/common/WhatsApp Image 2026-01-12 at 6.27.24 PM.jpeg";

export default function About() {
    return (
        <div className="overflow-hidden">

            {/* HERO */}
            <section className="relative h-[22rem] flex items-center justify-center">
                <img
                    src={abb}
                    alt="Sri Lanka Nature"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/60" />
                <motion.h1
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1 }}
                    className="relative text-4xl md:text-5xl font-bold text-white text-center"
                >
                    About Ranmali Tours
                </motion.h1>
            </section>

            {/* ABOUT CONTENT */}
            <motion.section
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="py-20 px-6 bg-gray-50"
            >
                <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-14 items-center">

                    {/* IMAGE */}
                    <img
                        src={about}
                        alt="Travel Experience"
                        className="rounded-2xl shadow-xl w-full object-cover"
                    />

                    {/* TEXT */}
                    <div>
                        <h2 className="text-4xl font-bold mb-6">
                            Your Trusted Travel Partner in Sri Lanka
                        </h2>

                        <p className="text-gray-700 leading-relaxed mb-4">
                            <strong>Ranmali Tours</strong> is a Sri Lanka–based travel company with over
                            <strong> 5 years of experience</strong> in delivering unforgettable travel experiences.
                            We specialize in tailor-made tours designed to match your interests, comfort, and budget.
                        </p>

                        <p className="text-gray-700 leading-relaxed mb-6">
                            From golden beaches and lush hill country to wildlife safaris and cultural heritage sites,
                            we take care of everything — so you can relax and enjoy every moment of your journey.
                        </p>

                        <a
                            href="/tours"
                            className="inline-block bg-yellow-400 text-black px-7 py-3 rounded-full font-semibold shadow hover:scale-105 transition"
                        >
                            Plan Your Trip With Us
                        </a>
                    </div>
                </div>
            </motion.section>

            {/* OUR SERVICES */}
            <motion.section
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="py-20 px-6 bg-white"
            >
                <div  data-aos="zoom-in" className="max-w-6xl mx-auto">
                    <h2 className="text-4xl font-bold text-center mb-14">
                        Our Services
                    </h2>

                    <div data-aos="zoom-in" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10">

                        <ServiceCard
                            icon={<Headphones />}
                            title="24-Hour Service"
                            desc="Round-the-clock support to assist you anytime during your journey."
                        />

                        <ServiceCard
                            icon={<Plane />}
                            title="Airport Transfers"
                            desc="Comfortable and punctual airport pickup and drop-off services."
                        />

                        <ServiceCard
                            icon={<Car />}
                            title="Private Luxury Vehicles"
                            desc="Modern, air-conditioned vehicles with professional drivers."
                        />

                        <ServiceCard
                            icon={<Hotel />}
                            title="Hotel Booking"
                            desc="Carefully selected hotels and resorts to suit your preferences."
                        />

                        <ServiceCard
                            icon={<MapPin />}
                            title="Private Guided Tours"
                            desc="Experienced local guides for cultural, nature, and adventure tours."
                        />

                        <ServiceCard
                            icon={<ShieldCheck />}
                            title="Tourist Assistance"
                            desc="Complete travel guidance, safety support, and local assistance."
                        />
                    </div>
                </div>
            </motion.section>

            {/* STATS */}
            <motion.section
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="py-16 px-6 bg-gray-900 text-white"
            >
                <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-12 text-center">
                    <div>
                        <h3 className="text-4xl font-bold text-yellow-400">5+</h3>
                        <p className="mt-2">Years of Experience</p>
                    </div>
                    <div>
                        <h3 className="text-4xl font-bold text-yellow-400">50+</h3>
                        <p className="mt-2">Tours Completed</p>
                    </div>
                    <div>
                        <h3 className="text-4xl font-bold text-yellow-400">1000+</h3>
                        <p className="mt-2">Happy Travelers</p>
                    </div>
                </div>
            </motion.section>
        </div>
    );
}

/* SERVICE CARD */
function ServiceCard({ icon, title, desc }: any) {
    return (
        <motion.div
            whileHover={{ y: -8 }}
            className="p-8 rounded-2xl bg-gray-50 shadow hover:shadow-xl transition text-center"
        >
            <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-yellow-400 text-black mb-5">
                {icon}
            </div>
            <h3 className="text-xl font-semibold mb-2">{title}</h3>
            <p className="text-gray-600 text-sm">{desc}</p>
        </motion.div>
    );
}
